
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, Package } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ProductVariant {
  id?: string;
  name: string;
  sku?: string;
  price?: number;
  compare_at_price?: number;
  stock_quantity: number;
  weight?: number;
  variant_options?: Record<string, string>;
  image_url?: string;
  is_active: boolean;
}

interface ProductVariantsManagerProps {
  variants: ProductVariant[];
  onVariantsChange: (variants: ProductVariant[]) => void;
}

const ProductVariantsManager = ({ variants, onVariantsChange }: ProductVariantsManagerProps) => {
  const { toast } = useToast();
  const [showAddForm, setShowAddForm] = useState(false);

  const addVariant = () => {
    const newVariant: ProductVariant = {
      id: `temp-${Date.now()}`,
      name: `Variant ${variants.length + 1}`,
      stock_quantity: 0,
      is_active: true,
      variant_options: {}
    };
    onVariantsChange([...variants, newVariant]);
    setShowAddForm(false);
    toast({
      title: "Variant Added",
      description: "New product variant has been added",
    });
  };

  const updateVariant = (index: number, field: keyof ProductVariant, value: any) => {
    const updated = [...variants];
    updated[index] = { ...updated[index], [field]: value };
    onVariantsChange(updated);
  };

  const removeVariant = (index: number) => {
    const updated = variants.filter((_, i) => i !== index);
    onVariantsChange(updated);
    toast({
      title: "Variant Removed",
      description: "Product variant has been removed",
    });
  };

  const duplicateVariant = (index: number) => {
    const original = variants[index];
    const duplicate: ProductVariant = {
      ...original,
      id: `temp-${Date.now()}`,
      name: `${original.name} (Copy)`,
      sku: original.sku ? `${original.sku}-copy` : undefined
    };
    onVariantsChange([...variants, duplicate]);
    toast({
      title: "Variant Duplicated",
      description: "Product variant has been duplicated",
    });
  };

  return (
    <Card className="admin-card border-admin-border">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-admin-foreground flex items-center gap-2">
            <Package className="h-5 w-5" />
            Product Variants
          </CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addVariant}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Variant
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {variants.length > 0 ? (
          <div className="space-y-4">
            {variants.map((variant, index) => (
              <Card key={variant.id || index} className="border border-admin-border">
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-admin-foreground">
                      Variant {index + 1}
                    </h4>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => duplicateVariant(index)}
                      >
                        Duplicate
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => removeVariant(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-admin-foreground">Variant Name</Label>
                      <Input
                        value={variant.name}
                        onChange={(e) => updateVariant(index, 'name', e.target.value)}
                        className="bg-admin-background border-admin-border text-admin-foreground"
                        placeholder="e.g., Small Red, Large Blue"
                      />
                    </div>

                    <div>
                      <Label className="text-admin-foreground">SKU</Label>
                      <Input
                        value={variant.sku || ''}
                        onChange={(e) => updateVariant(index, 'sku', e.target.value)}
                        className="bg-admin-background border-admin-border text-admin-foreground"
                        placeholder="Unique SKU code"
                      />
                    </div>

                    <div>
                      <Label className="text-admin-foreground">Price ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={variant.price || ''}
                        onChange={(e) => updateVariant(index, 'price', parseFloat(e.target.value) || undefined)}
                        className="bg-admin-background border-admin-border text-admin-foreground"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <Label className="text-admin-foreground">Compare Price ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={variant.compare_at_price || ''}
                        onChange={(e) => updateVariant(index, 'compare_at_price', parseFloat(e.target.value) || undefined)}
                        className="bg-admin-background border-admin-border text-admin-foreground"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <Label className="text-admin-foreground">Stock Quantity</Label>
                      <Input
                        type="number"
                        value={variant.stock_quantity}
                        onChange={(e) => updateVariant(index, 'stock_quantity', parseInt(e.target.value) || 0)}
                        className="bg-admin-background border-admin-border text-admin-foreground"
                        placeholder="0"
                      />
                    </div>

                    <div>
                      <Label className="text-admin-foreground">Weight (kg)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={variant.weight || ''}
                        onChange={(e) => updateVariant(index, 'weight', parseFloat(e.target.value) || undefined)}
                        className="bg-admin-background border-admin-border text-admin-foreground"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <Label className="text-admin-foreground">Status</Label>
                      <Select
                        value={variant.is_active ? 'active' : 'inactive'}
                        onValueChange={(value) => updateVariant(index, 'is_active', value === 'active')}
                      >
                        <SelectTrigger className="bg-admin-background border-admin-border text-admin-foreground">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-admin-foreground">Image URL</Label>
                      <Input
                        value={variant.image_url || ''}
                        onChange={(e) => updateVariant(index, 'image_url', e.target.value)}
                        className="bg-admin-background border-admin-border text-admin-foreground"
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-admin-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-admin-foreground">No variants added yet</p>
            <p className="text-sm">Click "Add Variant" to create product variations</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductVariantsManager;
